import {
  Carrier,
  ClientProductCarrierWithCarrier,
  RfpCoveragesResponse,
} from "../../../../types/rfpApi";
import { formatCurrency, formatDate } from "../../../../utils";

import { FINAL_DECISION_STATUS_LABELS } from "./constants";
import { isBrokerOfRecord, isLostBroker, isWonBor } from "./utils";
import { deriveCoverageStatus } from "./finalDecisionFormAdapters";
import {
  CoverageFormValues,
  FinalDecisionFormValues,
  FinalDecisionStatus,
  ReviewDataResponse,
  ReviewDataResponseCoverage,
  ReviewDataResponseCurrentCoverage,
} from "./types";

interface GetReviewDataParams {
  formValues: FinalDecisionFormValues;
  carrierMap: Map<number, Carrier>;
  productMap: Map<number, ClientProductCarrierWithCarrier>;
  rfpCoverages: RfpCoveragesResponse;
}

interface BaseFieldsReturn {
  winningCarrier: string | null;
  winningContractedCarrier: string | null;
  annualWrittenPremium: string | null;
  renewalDate: string | null;
  rateGuarantee: string | null;
  winningGaOverridePercentage: string | null;
  winningGaAnnualOverrideAmount: string | null;
  techCredits: string | null;
  numberOfPlansQuoted: string | null;
  notes: string | null;
  finalDecision: string | null;
}

export const getBooleanReview = (
  value: boolean | null | undefined,
): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  return value ? "Yes" : "No";
};

const validateIncumbentFields = (
  coverage: CoverageFormValues,
): Partial<ReviewDataResponseCurrentCoverage["isMissing"]> => {
  const missing: Partial<ReviewDataResponseCurrentCoverage["isMissing"]> = {};

  if (!coverage.incumbentProductId) missing.incumbentProductId = true;
  if (!coverage.incumbentAnnualPremium) missing.incumbentAnnualPremium = true;

  if (coverage.incumbentContractedCarrier === true) {
    if (!coverage.incumbentGaOverridePercentage)
      missing.incumbentGaOverridePercentage = true;
    if (!coverage.incumbentGaAnnualOverrideAmount)
      missing.incumbentGaAnnualOverrideAmount = true;
  }

  return missing;
};

const validateMovingCarriersFields = (
  coverage: CoverageFormValues,
): Partial<ReviewDataResponseCurrentCoverage["isMissing"]> => {
  const missing: Partial<ReviewDataResponseCurrentCoverage["isMissing"]> = {};
  if (!coverage.winningCarrierId) missing.winningCarrier = true;
  if (!coverage.annualWrittenPremium) missing.annualWrittenPremium = true;
  if (!coverage.renewalDate) missing.renewalDate = true;
  if (
    coverage.rateGuarantee === undefined ||
    coverage.rateGuarantee === null ||
    coverage.rateGuarantee === ""
  )
    missing.rateGuarantee = true;
  if (coverage.techCredits === undefined) missing.techCredits = true;

  if (coverage.winningContractedCarrier === true) {
    if (!coverage.winningGaOverridePercentage)
      missing.winningGaOverridePercentage = true;
    if (!coverage.winningGaAnnualOverrideAmount)
      missing.winningGaAnnualOverrideAmount = true;
  }

  return missing;
};

const validateRenewingFields = (
  coverage: CoverageFormValues,
): Partial<ReviewDataResponseCurrentCoverage["isMissing"]> => {
  const missing: Partial<ReviewDataResponseCurrentCoverage["isMissing"]> = {};

  if (!coverage.annualWrittenPremium) missing.annualWrittenPremium = true;
  if (!coverage.renewalDate) missing.renewalDate = true;
  if (
    coverage.rateGuarantee === undefined ||
    coverage.rateGuarantee === null ||
    coverage.rateGuarantee === ""
  )
    missing.rateGuarantee = true;
  if (coverage.techCredits === undefined) missing.techCredits = true;

  if (coverage.winningContractedCarrier === true) {
    if (!coverage.winningGaOverridePercentage)
      missing.winningGaOverridePercentage = true;
    if (!coverage.winningGaAnnualOverrideAmount)
      missing.winningGaAnnualOverrideAmount = true;
  }

  return missing;
};

const validateDecisionFields = (
  coverage: CoverageFormValues,
  finalDecision: FinalDecisionStatus,
): Partial<ReviewDataResponseCurrentCoverage["isMissing"]> => {
  if (finalDecision === FinalDecisionStatus.MovingCarriers) {
    return validateMovingCarriersFields(coverage);
  }

  if (finalDecision === FinalDecisionStatus.RenewingWithIncumbent) {
    return validateRenewingFields(coverage);
  }

  return {};
};

const validateCurrentCoverage = (
  coverage: CoverageFormValues,
  formValues: FinalDecisionFormValues,
): ReviewDataResponseCurrentCoverage["isMissing"] => {
  const missing: ReviewDataResponseCurrentCoverage["isMissing"] = {};

  // numberOfPlansQuoted is always required
  if (!coverage.numberOfPlansQuoted) {
    missing.numberOfPlansQuoted = true;
  }

  // If lostBrokerOfRecord is selected, validate incumbent fields only
  if (isLostBroker(formValues)) {
    Object.assign(missing, validateIncumbentFields(coverage));
    return Object.keys(missing).length > 0 ? missing : undefined;
  }

  // If not broker of record (wonBOR = "No" for prospects), only numberOfPlansQuoted is required
  if (!isWonBor(formValues)) {
    return Object.keys(missing).length > 0 ? missing : undefined;
  }

  // If broker of record, validate all fields
  Object.assign(missing, validateIncumbentFields(coverage));

  if (!coverage.finalDecisionSelected) {
    missing.finalDecisionSelected = true;
    return Object.keys(missing).length > 0 ? missing : undefined;
  }

  Object.assign(
    missing,
    validateDecisionFields(coverage, coverage.finalDecisionSelected),
  );

  return Object.keys(missing).length > 0 ? missing : undefined;
};

const validateNewCoverage = (
  coverage: CoverageFormValues,
  formValues: FinalDecisionFormValues,
): ReviewDataResponseCoverage["isMissing"] => {
  const missing: ReviewDataResponseCoverage["isMissing"] = {};

  // numberOfPlansQuoted is always required
  if (!coverage.numberOfPlansQuoted) {
    missing.numberOfPlansQuoted = true;
  }

  if (!isBrokerOfRecord(formValues)) {
    return Object.keys(missing).length > 0 ? missing : undefined;
  }

  if (!coverage.sold) {
    return Object.keys(missing).length > 0 ? missing : undefined;
  }

  if (!coverage.winningCarrierId) missing.winningCarrier = true;
  if (!coverage.annualWrittenPremium) missing.annualWrittenPremium = true;
  if (!coverage.renewalDate) missing.renewalDate = true;
  if (!coverage.rateGuarantee) missing.rateGuarantee = true;
  if (coverage.techCredits === undefined) missing.techCredits = true;

  if (coverage.winningContractedCarrier === true) {
    if (!coverage.winningGaOverridePercentage)
      missing.winningGaOverridePercentage = true;
    if (!coverage.winningGaAnnualOverrideAmount)
      missing.winningGaAnnualOverrideAmount = true;
  }

  return Object.keys(missing).length > 0 ? missing : undefined;
};

const getBaseFields = (
  coverage: CoverageFormValues,
  carrierMap: Map<number, Carrier>,
  formValues: FinalDecisionFormValues,
): BaseFieldsReturn => {
  const getCarrierName = (carrierId: number | undefined): string | null =>
    typeof carrierId === "number"
      ? (carrierMap.get(carrierId)?.name ?? null)
      : null;

  const getRenewalDateReview = (): string | null => {
    if (coverage.renewalDate) {
      return formatDate(coverage.renewalDate, true) ?? null;
    }
    return null;
  };

  const isRenewingWithIncumbent =
    coverage.finalDecisionSelected ===
    FinalDecisionStatus.RenewingWithIncumbent;

  return {
    winningCarrier: getCarrierName(coverage.winningCarrierId),
    winningContractedCarrier: isRenewingWithIncumbent
      ? null
      : getBooleanReview(coverage.winningContractedCarrier),
    annualWrittenPremium: coverage.annualWrittenPremium
      ? formatCurrency(coverage.annualWrittenPremium)
      : null,
    renewalDate: getRenewalDateReview(),
    rateGuarantee: coverage.rateGuarantee ?? null,
    winningGaOverridePercentage: coverage.winningGaOverridePercentage
      ? `${coverage.winningGaOverridePercentage}%`
      : null,
    winningGaAnnualOverrideAmount: coverage.winningGaAnnualOverrideAmount
      ? formatCurrency(coverage.winningGaAnnualOverrideAmount)
      : null,
    techCredits: getBooleanReview(coverage.techCredits),
    numberOfPlansQuoted: coverage.numberOfPlansQuoted ?? null,
    notes: coverage.notes ?? null,
    finalDecision:
      FINAL_DECISION_STATUS_LABELS[
        deriveCoverageStatus(formValues, coverage)
      ] ?? null,
  };
};

const getReviewData = ({
  formValues,
  carrierMap,
  productMap,
  rfpCoverages,
}: GetReviewDataParams): ReviewDataResponse => {
  const completeCarrierMap = new Map(carrierMap);
  productMap.forEach((product) => {
    if (product.carrier && !completeCarrierMap.has(product.carrier.id)) {
      completeCarrierMap.set(product.carrier.id, product.carrier);
    }
  });

  const currentCoveragesMap = new Map(
    formValues.currentCoverages?.map((c) => [c.rfpProductId, c]) ?? [],
  );
  const newCoveragesMap = new Map(
    formValues.newCoverages?.map((c) => [c.rfpProductId, c]) ?? [],
  );

  const currentCoverages =
    rfpCoverages.currentCoverages
      ?.map((rfpCoverage) => {
        const coverage = currentCoveragesMap.get(rfpCoverage.id);
        if (!coverage) {
          return null;
        }
        const incumbentProduct = coverage.incumbentProductId
          ? productMap.get(coverage.incumbentProductId)
          : null;
        const incumbentAnnualPremium = coverage.incumbentAnnualPremium
          ? formatCurrency(coverage.incumbentAnnualPremium)
          : null;
        const incumbentGaOverridePercentage =
          coverage.incumbentGaOverridePercentage
            ? `${coverage.incumbentGaOverridePercentage}%`
            : null;
        const incumbentGaAnnualOverrideAmount =
          coverage.incumbentGaAnnualOverrideAmount
            ? formatCurrency(coverage.incumbentGaAnnualOverrideAmount)
            : null;
        return {
          ...coverage,
          name: rfpCoverage.name,
          incumbentProduct: incumbentProduct
            ? `${incumbentProduct.groupNumber} - ${incumbentProduct.carrier.name}`
            : null,
          incumbentAnnualPremium,
          incumbentGaOverridePercentage,
          incumbentGaAnnualOverrideAmount,
          incumbentContractedCarrier: getBooleanReview(
            coverage.incumbentContractedCarrier,
          ),
          ...getBaseFields(coverage, completeCarrierMap, formValues),
          isMissing: validateCurrentCoverage(coverage, formValues),
        };
      })
      .filter((c) => c) ?? [];

  const newCoverages =
    rfpCoverages.newCoverages
      ?.map((rfpCoverage) => {
        const coverage = newCoveragesMap.get(rfpCoverage.id);
        if (!coverage) {
          return null;
        }

        return {
          ...coverage,
          name: rfpCoverage.name,
          ...getBaseFields(coverage, carrierMap, formValues),
          isMissing: validateNewCoverage(coverage, formValues),
        };
      })
      .filter((c) => c) ?? [];

  return { currentCoverages, newCoverages };
};

export const hasAnyMissingFields = (
  reviewData: ReviewDataResponse,
): boolean => {
  const currentHasMissing = reviewData.currentCoverages.some(
    (coverage) =>
      coverage.isMissing && Object.keys(coverage.isMissing).length > 0,
  );
  const newHasMissing = reviewData.newCoverages.some(
    (coverage) =>
      coverage.isMissing && Object.keys(coverage.isMissing).length > 0,
  );
  return currentHasMissing || newHasMissing;
};

export { getReviewData };
